import { formatScore } from './scoreFormat';
import { getResultIdentity } from './resultInsights';

export const UNKNOWN_CATEGORY_LABEL = 'Без категории';

const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
});

const dateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function normalizeCategory(category) {
  const normalized = typeof category === 'string' ? category.trim() : '';
  return normalized || UNKNOWN_CATEGORY_LABEL;
}

function normalizeCategoryKey(categoryKey, legacyCategory) {
  const normalizedKey = typeof categoryKey === 'string' ? categoryKey.trim() : '';
  if (normalizedKey) {
    return normalizedKey;
  }

  const legacy = typeof legacyCategory === 'string' ? legacyCategory.trim() : '';
  return legacy || UNKNOWN_CATEGORY_LABEL;
}

function toChartPoint(result) {
  const score = Number(result.score) || 0;
  const date = new Date(result.date);
  const resultId = result._id != null ? String(result._id) : null;

  return {
    id: resultId || `${result.date}-${score}`,
    resultId,
    date: dateFormatter.format(date),
    rawDate: result.date,
    score,
    passingLabel: dateTimeFormatter.format(date),
    level: result.interpretation?.level || null,
  };
}

function calendarDayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Подписи оси X и связь точки с конкретным прохождением: при нескольких прохождениях в один день показываем дату и время. */
function addPassingLabelsToSeries(points) {
  if (!points.length) {
    return points;
  }
  const sorted = [...points].sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
  const years = new Set(sorted.map((p) => new Date(p.rawDate).getFullYear()));
  const spansMultipleYears = years.size > 1;

  const shortDateWithYear = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const dayCounts = new Map();
  sorted.forEach((p) => {
    const k = calendarDayKey(new Date(p.rawDate));
    dayCounts.set(k, (dayCounts.get(k) || 0) + 1);
  });

  const shortLabelFor = (d) => (spansMultipleYears ? shortDateWithYear.format(d) : dateFormatter.format(d));

  const xAxisSeen = new Map();
  return sorted.map((p, index) => {
    const d = new Date(p.rawDate);
    const k = calendarDayKey(d);
    let xAxisLabel = dayCounts.get(k) > 1 ? dateTimeFormatter.format(d) : shortLabelFor(d);
    if (xAxisSeen.has(xAxisLabel)) {
      xAxisLabel = `${dateTimeFormatter.format(d)} · ${index + 1}`;
    }
    xAxisSeen.set(xAxisLabel, true);
    return {
      ...p,
      xAxisLabel,
    };
  });
}

export function buildAnalyticsCatalog(results = []) {
  const categoriesMap = new Map();

  results
    .filter((result) => result && result.date)
    .forEach((result) => {
      const identity = getResultIdentity(result);
      if (!identity) {
        return;
      }

      const categoryKey = normalizeCategoryKey(
        result.testSnapshot?.categoryKey,
        result.testSnapshot?.category,
      );
      const categoryName = normalizeCategory(
        result.testSnapshot?.categoryLabel || result.testSnapshot?.category,
      );
      const title = result.testSnapshot?.title || 'Неизвестный тест';
      const description = result.testSnapshot?.description || 'Описание методики недоступно.';

      if (!categoriesMap.has(categoryKey)) {
        categoriesMap.set(categoryKey, {
          name: categoryName,
          testsMap: new Map(),
        });
      }

      const categoryGroup = categoriesMap.get(categoryKey);
      if (categoryGroup.name === UNKNOWN_CATEGORY_LABEL && categoryName !== UNKNOWN_CATEGORY_LABEL) {
        categoryGroup.name = categoryName;
      }

      const testsMap = categoryGroup.testsMap;
      if (!testsMap.has(identity)) {
        testsMap.set(identity, {
          identity,
          title,
          description,
          points: [],
        });
      }

      testsMap.get(identity).points.push(toChartPoint(result));
    });

  return [...categoriesMap.values()]
    .sort((left, right) => left.name.localeCompare(right.name, 'ru-RU'))
    .map(({ name, testsMap }) => {
      const tests = [...testsMap.values()]
        .map((test) => ({
          ...test,
          points: addPassingLabelsToSeries(test.points),
        }))
        .sort((left, right) => left.title.localeCompare(right.title, 'ru-RU'));

      return {
        name,
        tests,
      };
    });
}

export function getProgressSummary(points = []) {
  if (points.length < 2) {
    return {
      tone: 'neutral',
      label: 'Недостаточно данных',
      description: 'Нужно минимум два прохождения, чтобы показать динамику.',
    };
  }

  const firstScore = Number(points[0].score) || 0;
  const lastScore = Number(points.at(-1).score) || 0;
  const delta = lastScore - firstScore;
  const direction = delta < 0 ? 'improvement' : delta > 0 ? 'worse' : 'same';
  const base = Math.max(Math.abs(firstScore), 1);
  const percent = Math.round((Math.abs(delta) / base) * 100);

  if (direction === 'same') {
    return {
      tone: 'neutral',
      label: 'Без изменений',
      description: `Сравнение первого и последнего результата: ${formatScore(lastScore)}.`,
    };
  }

  if (direction === 'improvement') {
    return {
      tone: 'success',
      label: 'Прогресс',
      description: `Баллы снизились на ${percent}%, с ${formatScore(firstScore)} до ${formatScore(lastScore)}.`,
    };
  }

  return {
    tone: 'warning',
    label: 'Требует внимания',
    description: `Баллы выросли на ${percent}%, с ${formatScore(firstScore)} до ${formatScore(lastScore)}.`,
  };
}

export function getLastActivityText(points = []) {
  if (!points.length) {
    return 'Прохождений пока нет';
  }

  const last = points.at(-1);
  if (last.passingLabel) {
    return `Последнее прохождение: ${last.passingLabel}`;
  }
  const lastDate = new Date(last.rawDate);
  return `Последнее прохождение: ${dateTimeFormatter.format(lastDate)}`;
}
